import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import { useState, useEffect } from "react";
import { Center, HStack, SimpleGrid, useToast } from "@chakra-ui/react";
import {
  Container,
  Box,
  VStack,
  StackDivider,
  Spacer,
  useColorModeValue,
  Button,
  Stack,
  Heading,
  Text,
  Show,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Portal,
  Input,
} from "@chakra-ui/react";
import { EditIcon, SettingsIcon } from "@chakra-ui/icons";
import { Link, useParams } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import { DataDump } from "../components/DeviceComponents/DataDump";
import { DeviceMap } from "../components/DeviceComponents/DeviceMap";
import DevicePlot from "../components/DeviceComponents/DevicePlot";
import { IDevice } from "../types/deviceInterface";
import { FaFileDownload } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import { isLteDevice } from "../utils/deviceUtils";
import { IField } from "../types/fieldInterface";
import { getMoistureIndexThresholds } from "../utils/colorUtils/mapColorUtils";

const DeviceViewer = () => {
  const api = useAxios();
  const toast = useToast();

  const { id } = useParams<{ id: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [device, setDevice] = useState<IDevice | undefined>(undefined);
  const [field, setField] = useState<IField | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const box_color = useColorModeValue("white", "gray.900");
  const heading_color = useColorModeValue("black", "gray.200");
  const formatDate = (date: Date | null) => (date ? date.toISOString().split("T")[0] : "");
  const [startDate, setStartDate] = useState<Date | null>(null);

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const maxDateStr = yesterday.toISOString().split("T")[0];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("frontend/devices/current-data/" + id + "/");
        console.log(response.data);
        setDevice(response.data);

        setIsLoading(false);
      } catch {
        setDevice(undefined);
        setIsLoading(false);
        // Toast
        toast({
          title: <Trans>Error fetching data</Trans>,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setField(null);
    if (device?.field_id == null) return;

    let cancelled = false;
    const fetchField = async () => {
      try {
        const response = await api.get<IField>(`agtech/fields/${device.field_id}/`);
        if (!cancelled) setField(response.data);
      } catch {
        if (!cancelled) {
          toast({
            title: <Trans>Error fetching crop limits</Trans>,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      }
    };
    fetchField();
    return () => {
      cancelled = true;
    };
  }, [device?.field_id]);

  const fetchRawData = async () => {
    if (!startDate) {
      toast({
        title: <Trans>Please select both start and end dates</Trans>,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const selected = new Date(startDate);
    const now = new Date();
    selected.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    if (selected >= now) {
      toast({
        title: <Trans>Start date must be before today</Trans>,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await api.post(
        "frontend/devices/get-data/" + id,
        {
          start_date: startDate.toISOString(),
        },
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;

      const filename = `device_${id}_data.csv`;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);
      console.log(response.data);
    } catch {
      toast({
        title: <Trans>Error fetching raw data</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const requestLocation = async () => {
    if (!id) return;

    setIsLocating(true);
    try {
      await api.post(`interface/sensors/${id}/request-gps/`);
      toast({
        title: <Trans>Location request sent. This might take some time</Trans>,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch {
      toast({
        title: <Trans>Unable to request location</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsLocating(false);
    }
  };

  const hasGps = isLteDevice(device);
  const locationRequestedAt = device?.location_data_timestamp
    ? new Date(device.location_data_timestamp).toLocaleString()
    : undefined;

  const locationControls = hasGps ? (
    <HStack mt={2} spacing={3} align="center">
      <Button
        as={Link}
        to={`/devices/${id}/settings`}
        size="sm"
        colorScheme="black"
        variant="outline"
        borderRadius={"unset"}
        _hover={{ bg: "blackAlpha.100" }}
        rightIcon={<SettingsIcon />}
      >
        <Trans>Device settings</Trans>
      </Button>
      <Button
        size="sm"
        colorScheme="black"
        variant="outline"
        borderRadius={"unset"}
        _hover={{ bg: "blackAlpha.100" }}
        onClick={requestLocation}
        isLoading={isLocating}
        rightIcon={<MdLocationOn />}
      >
        <Trans>Locate</Trans>
      </Button>
      {locationRequestedAt && <Text fontSize="sm">{locationRequestedAt}</Text>}
    </HStack>
  ) : null;

  return (
    <Navbar>
      <VStack mt={2} align="stretch">
        <SimpleGrid minChildWidth="350px" spacing="2">
          <Box padding="5" m={1}>
            <Stack direction={{ base: "column", sm: "row" }} mb={4}>
              <Heading as="h2" size="lg" mb={4} mr={6} color={heading_color}>
                {device?.device_name ? (
                  device.device_name
                ) : (
                  <>
                    <Trans>Device</Trans>
                    {" #"}
                    {device?.device_id}
                  </>
                )}
              </Heading>

              <Button
                as={Link}
                to={"/devices/" + id + "/edit"}
                pl={5}
                borderRadius={"unset"}
                p={2}
                flexShrink={0}
                fontStyle="semibold"
                _hover={{ bg: "blackAlpha.100" }}
                size="md"
                colorScheme="black"
                variant="outline"
                rightIcon={<EditIcon />}
              >
                <Trans>Edit</Trans>
              </Button>

              <Button
                as={Link}
                to={"/alarms/add?device=" + id}
                borderRadius={"unset"}
                flexShrink={0}
                size="md"
                colorScheme="black"
                variant="outline"
              >
                <Trans>Configure alarms</Trans>
              </Button>

              <Popover>
                <PopoverTrigger>
                  <Button
                    pl={5}
                    borderRadius={"unset"}
                    p={2}
                    flexShrink={0}
                    fontStyle="semibold"
                    _hover={{ bg: "blackAlpha.100" }}
                    size="md"
                    colorScheme="black"
                    variant="outline"
                    rightIcon={<FaFileDownload />}
                  >
                    <Trans>Get raw data</Trans>
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <Box>
                      <VStack p={4} spacing={4} alignItems="center">
                        <Text mb={1}>
                          <Trans>Choose the number of days of raw data you want to download:</Trans>
                        </Text>

                        <Box flex="1" width="100%">
                          <Text fontSize="sm" mb={1}>
                            <Trans>Select the day from which you want to download data</Trans>
                          </Text>
                          <Input
                            type="date"
                            value={formatDate(startDate)}
                            max={maxDateStr}
                            onChange={(e) =>
                              setStartDate(e.target.value ? new Date(e.target.value) : null)
                            }
                          />
                        </Box>
                        <Button
                          onClick={fetchRawData}
                          borderRadius={"unset"}
                          flexShrink={0}
                          fontStyle="semibold"
                          _hover={{ bg: "blackAlpha.100" }}
                          size="md"
                          colorScheme="black"
                          variant="outline"
                          rightIcon={<FaFileDownload />}
                        >
                          <Trans>Download</Trans>
                        </Button>
                      </VStack>
                    </Box>
                  </PopoverContent>
                </Portal>
              </Popover>
            </Stack>
            <Heading size="sm" mb={2}>
              <Trans>Current Data:</Trans>
            </Heading>
            <DataDump device={device} />
          </Box>
          <Show above="lg">
            <Box padding="5" m={1}>
              <Box height={300}>
                <DeviceMap device={device} />
              </Box>
              {locationControls}
            </Box>
          </Show>
        </SimpleGrid>
        <Center>
          <Box padding="5" m={1} width={{ base: "100%" }} maxWidth={"1300"} alignContent={"center"}>
            <DevicePlot moistureIndexThresholds={getMoistureIndexThresholds(field)} />
          </Box>
        </Center>
        <Show below="lg">
          <Box padding="5" m={1}>
            <Box height={300}>
              <DeviceMap device={device} />
            </Box>
            {locationControls}
          </Box>
        </Show>
      </VStack>
    </Navbar>
  );
};

export default DeviceViewer;
